import * as React from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/pro-solid-svg-icons";
import { loader } from "graphql.macro";
import {
  LeaderboardsQuery,
  LeaderboardsQueryVariables,
} from "../../graphql/LeaderboardsQuery.generated";
import { useQuery } from "../../hooks/useQuery";
import { AbsoluteCenter } from "../AbsoluteCenter";
import { Loader } from "../Loader";
import { TournamentFragment } from "../Area/graphql/TournamentFragment.generated";
import { useRelativeTimeFormatter } from "../../hooks/useRelativeTimeFormatter";
import { CurrencyImage } from "../CurrencyImage";
import { useNumberFormatter } from "../../hooks/useNumberFormatter";
import { useOrdinalFormatter } from "../../hooks/useOrdinalFormatter";
import { ButtonWithSound, LinkWithSound } from "../AudioProvider";
import { useIsInTable } from "../../hooks/useIsInTable";
import { useScreenType } from "../../hooks/useScreenType";
import {
  RIGHT_BAR_MOBILE_SIZE,
  RIGHT_BAR_TABLET_SIZE,
} from "../Bar/RightBar/Bar";
import {
  InstantTournamentsQuery,
  InstantTournamentsQueryVariables,
} from "../../graphql/InstantTournamentsQuery.generated";
import {
  ScheduledTournamentsQuery,
  ScheduledTournamentsQueryVariables,
} from "../../graphql/ScheduledTournamentsQuery.generated";
import {
  ScheduledTournamentFilterPeriod,
  ScheduledTournamentSortBy,
  TournamentType,
} from "../../graphql/generated";
import classNames from "classnames";
import { useHistory } from "react-router-dom";
import competition from "./assets/competitions.jpg";
import tournamentsBg from "./assets/tournaments.jpg";
import mpirimpaBg from "./assets/mpirimpa.jpg";
import mpirimpaELBg from "./assets/mpirimpaEL.png";
import tichuBg from "./assets/tichuBg.png";
import beloteBg from "./assets/beloteBg.png";
import coincheBg from "./assets/coincheBg.png";
import backgammonClassicBg from "./assets/backgammonClassicBg.png";
import backgammonPortesBg from "./assets/backgammonPortesBg.png";

const TournamentsContext = React.createContext<(open: boolean) => void>(() => {
  throw new Error("Provider not found");
});

const LEADERBOARDS_QUERY = loader("../../graphql/LeaderboardsQuery.graphql");
const INSTANT_TOURNAMENT_QUERY = loader(
  "../../graphql/InstantTournamentsQuery.graphql"
);
const SCHEDULED_TOURNAMENT_QUERY = loader(
  "../../graphql/ScheduledTournamentsQuery.graphql"
);

const gameBackgrounds: Record<string, string> = {
  mpirimpa_el: mpirimpaELBg,
  mpirimpa_cy: mpirimpaBg,
  tichu: tichuBg,
  belote: beloteBg,
  coinche: coincheBg,
  backgammon_classic: backgammonClassicBg,
  backgammon_portes: backgammonPortesBg,
};

function Tournament({ value }: { value: TournamentFragment }) {
  const { t } = useTranslation();
  const inTable = useIsInTable();
  const relativeTimeFormatter = useRelativeTimeFormatter();
  const numberFormatter = useNumberFormatter();
  const ordinalFormatter = useOrdinalFormatter();
  const firstPrice = value.prices.find((p) => p.from <= 1 && p.to >= 1);
  return (
    <div className="bg-blue-900 rounded p-2">
      <div className="flex justify-between text-white flex-shrink-0 -mx-1 overflow-hidden">
        <div className="px-1">
          <img
            src={`${process.env.REACT_APP_API_HOST}/tournaments/${value.logo}`}
            className="rounded bg-white"
            alt="logo"
            width={52}
            height={52}
          />
        </div>
        <div className="flex-1 flex flex-col px-1 text-white overflow-hidden">
          <div className="flex -mx-1 items-center justify-between">
            <div className="px-1 truncate">
              <span className="font-bold text-lg truncate">{value.name}</span>
            </div>
            <div className="px-1 flex-shrink-0">
              {t(
                value.active ? "leader_board_expiry" : "leader_board_completed",
                {
                  expiry: relativeTimeFormatter.format(new Date(value.end), {
                    addSuffix: true,
                  }),
                }
              )}
            </div>
          </div>
          <div>{t(`game.${value.section.type}`)}</div>
        </div>
      </div>
      {firstPrice !== undefined && (
        <div className="mt-4 text-white">
          <div className="flex -mx-2">
            <div className="px-2 flex-1 flex flex-col justify-between">
              <span className="text-sm leading-none font-bold block">
                {firstPrice.from !== firstPrice.to
                  ? `${ordinalFormatter.format(
                      firstPrice.from
                    )} - ${ordinalFormatter.format(firstPrice.to)}`
                  : `${ordinalFormatter.format(firstPrice.from)}`}
              </span>
              <span className="text-xs">
                {`${numberFormatter.format(firstPrice.amount.value)} ${t(
                  `currency.${firstPrice.amount.currency.toLowerCase()}`
                )}`}
              </span>
            </div>
            <div className="px-2 flex-shrink-0 flex items-center">
              <CurrencyImage
                currency={firstPrice.amount.currency}
                width={25}
                height={25}
              />
            </div>
          </div>
        </div>
      )}
      <div className="mt-4 text-white">
        <div className="flex -mx-2">
          <div className="px-2 flex-1 flex flex-col justify-between">
            <span className="text-sm leading-none font-bold block">
              {t("entry_fee")}
            </span>
            <span className="text-xs">
              {t("tournament_entry_fee_details", {
                price: `${numberFormatter.format(value.amount.value)} ${t(
                  `currency.${value.amount.currency.toLowerCase()}`
                )}`,
                games: value.gamesPerPurchase,
              })}
            </span>
          </div>
          <div className="px-2 flex-shrink-0 flex items-center">
            <CurrencyImage
              currency={value.amount.currency}
              width={25}
              height={25}
            />
          </div>
        </div>
      </div>
      {!inTable && (
        <div className="mt-4">
          <LinkWithSound
            className="btn btn-orange-600 rounded w-full"
            to={`/area/${value.section.id}`}
          >
            <div className="flex -mx-1 items-center justify-center">
              <span className="px-1 text-center flex-1 truncate text-white">
                {t("view")}
              </span>
            </div>
          </LinkWithSound>
        </div>
      )}
    </div>
  );
}

const TournamentItem = ({
  backgroundImage,
  title,
  description,
  buttonText,
  action,
}: {
  backgroundImage: string;
  title: string;
  description: string;
  buttonText: string;
  action: () => void;
}) => {
  return (
    <div
      className={classNames(
        "flex",
        "flex-col",
        "justify-center",
        "text-white",
        "transition-all",
        "duration-300",
        "ease-in-out",
        "font-bold",
        "py-1",
        "px-4",
        "bg-gray-500",
        "bg-blue-500",
        "gap-5"
      )}
      style={{
        height: "11.25rem",
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        borderRadius: "8px",
        fontSize: "18px",
      }}
    >
      <div className="flex flex-col gap-1">
        <div>{title}</div>
        <div
          style={{
            fontSize: "16px",
            fontWeight: 400,
          }}
        >
          {description}
        </div>
      </div>
      <ButtonWithSound
        className="btn btn-orange-600 rounded-lg text-white px-6"
        style={{
          fontSize: "14px",
          fontWeight: 500,
          width: "max-content",
        }}
        onClick={action}
      >
        {buttonText}
      </ButtonWithSound>
    </div>
  );
};

const TournamentsContent = React.memo(
  ({
    setShowCompetitions,
    showCompetitions,
    setShowTournaments,
    showTournaments,
    closeWindow,
  }: {
    setShowCompetitions: (val: boolean) => void;
    closeWindow: () => void;
    showCompetitions: boolean;
    setShowTournaments: (val: boolean) => void;
    showTournaments: boolean;
  }) => {
    const { push } = useHistory();

    const [{ data }] = useQuery<LeaderboardsQuery, LeaderboardsQueryVariables>({
      query: LEADERBOARDS_QUERY,
      variables: {
        filter: {
          rooms: [],
          sections: [],
        },
      },
    });
    const [{ data: instantData, fetching: instantFetching }] = useQuery<
      InstantTournamentsQuery,
      InstantTournamentsQueryVariables
    >({
      query: INSTANT_TOURNAMENT_QUERY,
      variables: {
        filter: {
          roomId: null,
          gameId: null,
          onlyMine: false,
          onlyActive: true,
          statuses: [],
          playersPerTeam: [],
        },
      },
    });

    const now = new Date();
    const offsetMinutes = now.getTimezoneOffset();

    const [{ data: scheduledData, fetching: scheduledFetching }] = useQuery<
      ScheduledTournamentsQuery,
      ScheduledTournamentsQueryVariables
    >({
      query: SCHEDULED_TOURNAMENT_QUERY,
      variables: {
        filter: {
          roomId: null,
          gameId: null,
          onlyMine: false,
          onlyActive: true,
          statuses: [],
          playersPerTeam: [],
          period: {
            periodType: ScheduledTournamentFilterPeriod.All,
            timezoneOffsetMinutes: offsetMinutes,
          },
          sortBy: ScheduledTournamentSortBy.SoonestStart,
        },
      },
    });

    const gameNames = Object.keys(gameBackgrounds);

    const tournamentsData = React.useMemo(() => {
      const arr = [];
      for (const gameId of gameNames) {
        const instant = instantData?.instantTournaments?.tournaments?.find(
          (t) => t.gameId === gameId
        );

        if (instant) {
          arr.push({
            gameId: instant.gameId,
            roomId: instant.roomId,
            isInstant: true,
          });
        }

        const scheduled = scheduledData?.scheduledTournaments?.tournaments?.find(
          (t) => t.gameId === gameId
        );

        if (scheduled) {
          arr.push({
            gameId: scheduled.gameId,
            roomId: scheduled.roomId,
            isInstant: false,
          });
        }
      }

      return arr;
    }, [scheduledData, instantData]);

    const { t } = useTranslation();

    const handleRedirect = (roomId: string, gameId: string, type: string) => {
      closeWindow();
      push(`/area/${roomId}:${gameId}/${type}`);
    };

    const getCurrentBackground = (gameName: string) => {
      return gameBackgrounds[gameName];
    };

    if (data === undefined || scheduledFetching || instantFetching)
      return (
        <div className="flex-1 relative">
          <AbsoluteCenter>
            <Loader description={t("loading_tournaments")} />
          </AbsoluteCenter>
        </div>
      );
    else {
      const tournaments =
        data?.leaderboards?.filter(
          (l) => l.__typename === "Tournament" && l.active
        ) ?? [];

      if (tournaments.length === 0 && !tournamentsData?.length) {
        return (
          <div className="flex-1 relative">
            <AbsoluteCenter>
              <div className="p-4 text-center rounded bg-red-700">
                <span className="text-white font-bold block">
                  {t("instant_tournament.no_tournaments")}
                </span>
              </div>
            </AbsoluteCenter>
          </div>
        );
      } else {
        if (showCompetitions) {
          return (
            <div className="pt-2">
              {tournaments.map((t) => (
                <div className="py-2" key={t.id}>
                  <Tournament value={t as any} />
                </div>
              ))}
            </div>
          );
        } else if (showTournaments) {
          return (
            <div>
              {tournamentsData?.map((tournament) => {
                return (
                  <div className="pt-2">
                    <div
                      className={classNames(
                        "flex",
                        "flex-col",
                        "gap-5",
                        "text-white",
                        "transition-all",
                        "duration-300",
                        "ease-in-out",
                        "text-white",
                        "font-bold",
                        "bg-gray-500"
                      )}
                      style={{
                        height: "8.4rem",
                        backgroundImage: `url(${getCurrentBackground(
                          tournament.gameId
                        )})`,
                        backgroundSize: "cover",
                        backgroundRepeat: "no-repeat",
                        borderRadius: "8px",
                        padding: "40px 16px 20px",
                      }}
                    >
                      <div>{t(`game.${tournament?.gameId}`)}</div>
                      <ButtonWithSound
                        className="btn btn-orange-600 rounded-lg text-white px-6 w-full"
                        style={{
                          fontSize: "14px",
                          fontWeight: 500,
                        }}
                        onClick={() =>
                          handleRedirect(
                            tournament.roomId,
                            tournament.gameId,
                            tournament.isInstant
                              ? "instant-tournaments"
                              : "scheduled-tournaments"
                          )
                        }
                      >
                        {t("view")}
                      </ButtonWithSound>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        } else {
          return (
            <div className="flex flex-col gap-4 pt-2">
              {!!tournaments?.length && (
                <TournamentItem
                  backgroundImage={competition}
                  title={t("tournaments")}
                  description={t("competitions_description")}
                  buttonText={t("view_competitions")}
                  action={() => setShowCompetitions(true)}
                />
              )}
              {!!tournamentsData?.length && (
                <TournamentItem
                  backgroundImage={tournamentsBg}
                  title={t("tournaments_new")}
                  description={t("tournaments_description")}
                  buttonText={t("view_tournaments")}
                  action={() => setShowTournaments(true)}
                />
              )}
            </div>
          );
        }
      }
    }
  }
);

const Popup = ({ close }: { close: () => void }) => {
  const { t } = useTranslation();
  const screenType = useScreenType();
  const [showCompetitions, setShowCompetitions] = React.useState(false);
  const [showTournaments, setShowTournaments] = React.useState(false);

  const stylesMap = {
    DESKTOP: { bottom: 0, left: 0 },
    TABLET: { top: 0, right: 0, ...RIGHT_BAR_TABLET_SIZE },
    MOBILE: { top: 0, right: 0, ...RIGHT_BAR_MOBILE_SIZE },
  };

  const adjustedStyle = React.useMemo(() => stylesMap[screenType] || {}, [
    screenType,
  ]);

  const closeWindow = () => {
    setShowCompetitions(false);
    close();
  };

  const handleClose = () => {
    if (showCompetitions || showTournaments) {
      setShowCompetitions(false);
      setShowTournaments(false);
    } else {
      close();
    }
  };

  return (
    <>
      <div
        className="absolute bg-black-alpha-30 top-0 left-0 right-0 bottom-0 z-20"
        onClick={closeWindow}
      />
      <div
        className="w-1/3 h-9/10 absolute shadow-2xl bg-blue-800 z-30"
        style={{ ...adjustedStyle }}
        aria-label={t("tournaments_new")}
      >
        <div className="flex flex-col h-full p-4">
          <div className="flex items-center justify-between text-white flex-shrink-0 -mx-2">
            <span className="font-bold text-lg truncate px-2">
              {t("tournaments_new")}
            </span>
            <div className="px-2 flex-shrink-0">
              <button onClick={handleClose}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>
          <TournamentsContent
            setShowCompetitions={setShowCompetitions}
            showCompetitions={showCompetitions}
            setShowTournaments={setShowTournaments}
            showTournaments={showTournaments}
            closeWindow={closeWindow}
          />
        </div>
      </div>
    </>
  );
};

export const TournamentsPanelProvider = ({
  children,
}: {
  children?: React.ReactNode;
}) => {
  const [isOpen, setOpen] = React.useState<boolean>(false);
  const close = React.useCallback(() => setOpen(false), []);

  return (
    <TournamentsContext.Provider value={setOpen}>
      {children}
      {isOpen && <Popup close={close} />}
    </TournamentsContext.Provider>
  );
};

export const useTournamentsPanel = () => {
  return React.useContext(TournamentsContext);
};
